/**
 * Contrôleur de la section produit maison : galerie (vignettes), sélection
 * de variante (pastilles/boutons), stepper de quantité.
 *
 * Custom element + délégation d'événements : la page navigue par morph
 * (voir view-transitions.js), les scripts inline de section ne s'exécutent
 * donc pas — ici connectedCallback couvre l'insertion, et la délégation
 * couvre le remplacement des enfants par un patch morph.
 */
class ProduitMaisonRoot extends HTMLElement {
  #bound = false;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const thumb = target.closest('[data-thumb]');
      if (thumb) {
        this.#showSlide(Number(thumb.dataset.index));
        return;
      }

      const qtyInput = this.querySelector('input[name="quantity"]');
      if (qtyInput && target.closest('[data-qty-minus]')) {
        qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1) - 1));
      } else if (qtyInput && target.closest('[data-qty-plus]')) {
        qtyInput.value = String(Number(qtyInput.value || 1) + 1);
      }
    });

    this.addEventListener('change', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.matches('[data-option-input]')) this.#onVariantChange();
    });
  }

  /** Groupe de galerie visible : chaque coloris a le sien, rendu en Liquid. */
  #activeGallery() {
    return this.querySelector('[data-gallery-group]:not([hidden])') || this;
  }

  #showSlide(index) {
    const scope = this.#activeGallery();
    const slides = scope.querySelectorAll('[data-slide]');
    if (!slides[index]) return;
    slides.forEach((slide, i) => (slide.hidden = i !== index));
    scope.querySelectorAll('[data-thumb]').forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  /**
   * Bascule sur le groupe de galerie d'une clé donnée.
   * @returns {boolean} vrai si le groupe affiché a changé.
   */
  #showGallery(key) {
    if (!key) return false;
    const groups = Array.from(this.querySelectorAll('[data-gallery-group]'));
    const next = groups.find((group) => group.dataset.galleryGroup === key);
    if (!next) return false;
    if (!next.hidden) return false;
    groups.forEach((group) => (group.hidden = group !== next));
    return true;
  }

  #onVariantChange() {
    const source = this.querySelector('[data-variants]');
    if (!source) return;

    /** @type {{id: number, available: boolean, price: string, options: string[], media: number, gallery: string, coloris: boolean}[]} */
    let variants;
    try {
      variants = JSON.parse(source.textContent || '[]');
    } catch {
      return;
    }

    const selected = Array.from(this.querySelectorAll('[data-option-input]'))
      .filter((input) => input instanceof HTMLInputElement && input.checked)
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map((input) => input.value);

    const variant = variants.find((v) => v.options.every((option, i) => option === selected[i]));
    if (!variant) return;

    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variant.id);

    // Chaque bloc prix garde le format choisi dans l'éditeur (data-price-format).
    this.querySelectorAll('[data-price]').forEach((priceEl) => {
      const format = priceEl instanceof HTMLElement ? priceEl.dataset.priceFormat : undefined;
      priceEl.textContent = (format && variant.price_formats?.[format]) || variant.price;
    });

    this.querySelectorAll('[data-selected-value]').forEach((label) => {
      const value = selected[Number(label.dataset.position) - 1];
      if (value !== undefined) label.textContent = value;
    });

    const atcBtn = this.querySelector('[data-atc]');
    const atcText = this.querySelector('[data-atc-text]');
    if (atcBtn instanceof HTMLButtonElement && atcText) {
      atcBtn.disabled = !variant.available;
      atcText.textContent = variant.available ? atcBtn.dataset.textAdd : atcBtn.dataset.textSoldout;
    }

    // Nouveau coloris : la galerie repart sur sa première image. Sinon on
    // conserve le comportement d'origine (image liée à la variante).
    const switched = this.#showGallery(variant.gallery);
    if (switched || variant.coloris) this.#showSlide(0);
    else if (variant.media > 0) this.#showSlide(variant.media - 1);

    if (this.dataset.productUrl) {
      history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
    }
  }
}

if (!customElements.get('produit-maison-root')) {
  customElements.define('produit-maison-root', ProduitMaisonRoot);
}
